import { useQuery, useMutation } from "@tanstack/react-query"
import { getVehicleImageFile, getVehicleImageIds } from "api/vehicleStockApi"
import { useEffect, useState } from "react"

export const VEHICLE_IMAGE_QUERY_KEY = "vehicle-images"

// Web Share API typically has limits around 10-20MB total
const MAX_SHARE_SIZE_BYTES = 15 * 1024 * 1024 // 15MB
const MAX_FILES_PER_SHARE = 10 // Increased since you're using 10

// Format bytes to human readable format
const formatFileSize = bytes => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
}

// ✅ Get Image IDs
export const useVehicleImageIds = (vehicleId, enabled = true) => {
  return useQuery({
    queryKey: [VEHICLE_IMAGE_QUERY_KEY, vehicleId],
    queryFn: () => getVehicleImageIds(vehicleId),
    enabled: !!vehicleId && enabled,
  })
}

// ✅ Fetch files with size information and warnings
export const fetchVehicleImageFiles = async (vehicleId, imageIds) => {
  if (!imageIds?.length) return []

  const results = await Promise.allSettled(
    imageIds.map(item => getVehicleImageFile(vehicleId, item.id, item.imgType))
  )

  const successfulResults = results
    .filter(r => r.status === "fulfilled")
    .map(r => r.value)

  return successfulResults
}

// ✅ Better error detection for Web Share API
const detectShareErrorType = error => {
  if (!error) return "UNKNOWN_ERROR"

  // Check for specific error names first
  if (error.name === "NotAllowedError") {
    return "PERMISSION_DENIED"
  }
  if (error.name === "AbortError") {
    return "USER_CANCELLED"
  }
  if (error.name === "TypeError") {
    return "INVALID_ARGUMENT"
  }

  // Check error message for specific patterns
  const errorMessage = error.toString().toLowerCase()

  if (
    errorMessage.includes("permission denied") ||
    errorMessage.includes("not allowed")
  ) {
    return "PERMISSION_DENIED"
  }

  if (
    errorMessage.includes("cancelled") ||
    errorMessage.includes("aborted") ||
    errorMessage.includes("dismissed")
  ) {
    return "USER_CANCELLED"
  }

  if (
    errorMessage.includes("too large") ||
    errorMessage.includes("exceed") ||
    errorMessage.includes("size limit") ||
    errorMessage.includes("quota")
  ) {
    return "SHARE_TOO_LARGE"
  }

  if (
    errorMessage.includes("not supported") ||
    errorMessage.includes("cannot share") ||
    errorMessage.includes("invalid")
  ) {
    return "NOT_SUPPORTED"
  }

  return "UNKNOWN_ERROR"
}

// ✅ Share files with better error handling
const shareFiles = async (files, chunkIndex) => {
  if (!files.length) return false

  const chunkInfo = chunkIndex !== undefined ? ` (chunk ${chunkIndex + 1})` : ""
  const totalSize = files.reduce((sum, file) => sum + file.size, 0)

  console.log(
    `📤 Sharing ${files.length} files${chunkInfo}, total size: ${formatFileSize(
      totalSize
    )}`
  )

  try {
    // Check if Web Share API is available
    if (!navigator.share) {
      throw new Error("Web Share API not supported in this browser")
    }

    // Check if files can be shared
    if (navigator.canShare && !navigator.canShare({ files })) {
      throw new Error("These files cannot be shared")
    }

    await navigator.share({ files })
    console.log(`✅ Successfully shared ${files.length} files${chunkInfo}`)
    return true
  } catch (error) {
    const errorType = detectShareErrorType(error)

    console.warn(`❌ Share failed${chunkInfo}:`, {
      errorType,
      error: error.toString(),
      fileCount: files.length,
      totalSize: formatFileSize(totalSize),
    })

    switch (errorType) {
      case "USER_CANCELLED":
        throw new Error("Share was cancelled by user")

      case "PERMISSION_DENIED":
        throw new Error("Share permission denied - check browser settings")

      case "SHARE_TOO_LARGE":
        console.warn(
          `🚨 Share too large${chunkInfo}: ${formatFileSize(totalSize)}`
        )
        throw new Error("SHARE_TOO_LARGE")

      case "NOT_SUPPORTED":
        throw new Error("Sharing not supported for these files")

      default:
        throw new Error(`Sharing failed: ${error.message}`)
    }
  }
}

// ✅ Mutation for sharing images with better error handling
export const useShareVehicleImages = () => {
  return useMutation({
    mutationFn: async ({ vehicleId, imageIds, onProgress }) => {
      // Check support first
      if (!navigator.share) {
        throw new Error("BROWSER_NOT_SUPPORTED")
      }

      if (
        navigator.canShare &&
        !navigator.canShare({ files: [new File([], "test")] })
      ) {
        throw new Error("FILE_SHARING_NOT_SUPPORTED")
      }

      if (!vehicleId || !imageIds?.length) {
        throw new Error("Vehicle ID and image IDs are required")
      }

      console.log(`🔄 Starting share process for ${imageIds.length} images`)
      console.log(
        `📊 Share limits: ${MAX_FILES_PER_SHARE} files, ${formatFileSize(
          MAX_SHARE_SIZE_BYTES
        )} total`
      )

      // Check if Web Share API is available
      if (!navigator.share) {
        throw new Error("Your browser doesn't support sharing files")
      }

      // Fetch all image files
      const blobs = await fetchVehicleImageFiles(vehicleId, imageIds)

      if (!blobs.length) {
        throw new Error("No images available to share")
      }

      const totalSize = blobs.reduce((sum, blob) => sum + blob.size, 0)
      console.log(
        `📋 Files to share: ${blobs.length} files, ${formatFileSize(
          totalSize
        )} total`
      )

      let totalShared = 0
      const totalToShare = blobs.length

      // Try to share all files at once first
      try {
        const success = await shareFilesInChunks(blobs)
        if (success) {
          totalShared = blobs.length
          onProgress?.((i + 1) * chunk.length, blobs.length)
          return {
            sharedCount: totalShared,
            totalCount: totalToShare,
            chunked: false,
          }
        }
      } catch (error) {
        // Only use chunking for size-related errors
        if (error.message === "SHARE_TOO_LARGE") {
          console.warn(
            "Single share failed due to size, would switch to chunked sharing here"
          )
          // Your chunking logic would go here
        }
        // Re-throw other errors (permission denied, user cancelled, etc.)
        throw error
      }

      return {
        sharedCount: totalShared,
        totalCount: totalToShare,
        chunked: false,
      }
    },

    onSuccess: data => {
      const { sharedCount, totalCount } = data

      if (sharedCount === 0) {
        console.warn("❌ No images were shared")
      } else {
        console.log(`✅ Successfully shared ${sharedCount} images!`)
      }
    },

    onError: error => {
      console.error("❌ Image sharing failed:", error.message)

      // Provide more user-friendly error messages
      if (error.message.includes("permission denied")) {
        console.error("💡 Tip: Check browser permissions for sharing")
      } else if (error.message.includes("not supported")) {
        console.error(
          "💡 Tip: Try using a different browser that supports Web Share API"
        )
      } else if (error.message.includes("cancelled")) {
        console.log("ℹ️  User cancelled the share operation")
      }
    },
  })
}

// ✅ Simple hook for basic sharing
export const useSimpleShareVehicleImages = () => {
  const mutation = useShareVehicleImages()

  return {
    ...mutation,
    mutate: (vehicleId, imageIds) => mutation.mutate({ vehicleId, imageIds }),
  }
}

// ✅ Hook to check if sharing is supported
export const useShareSupport = () => {
  return {
    isSupported: !!navigator.share,
    canShareFiles:
      !!navigator.share &&
      (!navigator.canShare ||
        navigator.canShare({ files: [new File([], "test.jpg")] })),
    limits: {
      maxFiles: MAX_FILES_PER_SHARE,
      maxSize: MAX_SHARE_SIZE_BYTES,
      maxSizeFormatted: formatFileSize(MAX_SHARE_SIZE_BYTES),
    },
  }
}

export const chunkFiles = (
  files,
  maxFilesPerChunk = 10,
  maxSizePerChunk = 15 * 1024 * 1024
) => {
  const chunks = []
  let currentChunk = []
  let currentSize = 0

  for (const file of files) {
    if (
      currentChunk.length >= maxFilesPerChunk ||
      currentSize + file.size > maxSizePerChunk
    ) {
      chunks.push(currentChunk)
      currentChunk = []
      currentSize = 0
    }

    currentChunk.push(file)
    currentSize += file.size
  }

  if (currentChunk.length) chunks.push(currentChunk)

  return chunks
}

export const shareFilesInChunks = async files => {
  const chunks = chunkFiles(files, 10, 15 * 1024 * 1024)

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i]

    try {
      console.log(`Sharing chunk ${i + 1}/${chunks.length}...`)
      await navigator.share({ files: chunk })
      console.log(`✅ Chunk ${i + 1} shared successfully!`)
    } catch (err) {
      console.error(`❌ Failed to share chunk ${i + 1}:`, err)
      // Optionally stop sharing remaining chunks
      throw err
    }
  }

  console.log("✅ All chunks shared successfully!")
}
export const usePrefetchedVehicleFiles = (vehicleId) => {
  const { data } = useVehicleImageIds(vehicleId) // fetch image IDs
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!vehicleId || !data) return

    // Combine vehicle and auction IDs
    const imageIds = [
      ...(data?.image_ids ?? []).map((id) => ({ id, imgType: "vehicle" })),
      ...(data?.auction_id ? [{ id: data.auction_id, imgType: "auction" }] : []),
    ]

    setLoading(true)

    fetchVehicleImageFiles(vehicleId, imageIds)
      .then((fetchedFiles) => {
        setFiles(fetchedFiles)
      })
      .finally(() => setLoading(false))
  }, [vehicleId, data])

  return { files, loading }
}
